import React, { useMemo, useState } from "react";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { createClient } from "@supabase/supabase-js";
import MobileLoadingSplash from "../pages/MobileLoadingSplash";
import { supabase } from "../supabaseClient";
import {
  encryptStorePassword,
  persistMultiStoreCredentialsForPin,
  upsertEncryptedStoreCredential,
} from "../utils/storeCredentialsCrypto";

export { STORED_MULTI_STORE_CREDENTIALS_KEY } from "../utils/storeCredentialsCrypto";

const ReceiptDashboardAuthScreen = ({
  session,
  authLoading,
  authEmail,
  setAuthEmail,
  authPassword,
  setAuthPassword,
  authError,
  setAuthError,
  authMode,
  setAuthMode,
  handleLogin,
  handleSignup,
}) => {
  const [signInTab, setSignInTab] = useState("sign-in");
  const [multiStorePin, setMultiStorePin] = useState("");
  const [isSubmittingMultiStoreLogin, setIsSubmittingMultiStoreLogin] =
    useState(false);
  const [multiStoreCredentials, setMultiStoreCredentials] = useState([
    {
      id: Date.now(),
      email: "",
      password: "",
      status: "idle",
      statusMessage: "",
    },
  ]);
  const validationSupabase = useMemo(() => {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!url || !anonKey) return null;
    return createClient(url, anonKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
    });
  }, []);
  const isAnyCredentialChecking = multiStoreCredentials.some(
    (store) => store.status === "checking",
  );

  const addMultiStoreCredential = () => {
    setMultiStoreCredentials((prev) => [
      ...prev,
      {
        id: Date.now() + Math.floor(Math.random() * 1000),
        email: "",
        password: "",
        status: "idle",
        statusMessage: "",
      },
    ]);
  };

  const removeMultiStoreCredential = (id) => {
    setMultiStoreCredentials((prev) => {
      const next = prev.filter((store) => store.id !== id);
      if (next.length > 0) return next;
      return [
        {
          id: Date.now() + Math.floor(Math.random() * 1000),
          email: "",
          password: "",
          status: "idle",
          statusMessage: "",
        },
      ];
    });
  };

  const updateMultiStoreCredential = (id, field, value) => {
    setMultiStoreCredentials((prev) =>
      prev.map((store) =>
        store.id === id
          ? {
              ...store,
              [field]: value,
              status: "idle",
              statusMessage: "",
            }
          : store,
      ),
    );
  };

  const validateMultiStoreCredential = async (id) => {
    const store = multiStoreCredentials.find((item) => item.id === id);
    if (!store) return false;
    if (!store.email?.trim() || !store.password?.trim()) return false;
    if (!validationSupabase) return false;

    setMultiStoreCredentials((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status: "checking",
              statusMessage: "Checking credentials...",
            }
          : item,
      ),
    );

    try {
      const { error } = await validationSupabase.auth.signInWithPassword({
        email: store.email.trim(),
        password: store.password,
      });

      if (error) {
        setMultiStoreCredentials((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: "invalid",
                  statusMessage: "Invalid email or password.",
                }
              : item,
          ),
        );
        return false;
      }

      await validationSupabase.auth.signOut();
      if (store.password && multiStorePin && window.crypto?.subtle) {
        try {
          const encryptedPayload = await encryptStorePassword(
            store.password,
            multiStorePin,
          );
          upsertEncryptedStoreCredential({
            email: store.email.trim(),
            encryptedPassword: encryptedPayload.encryptedPassword,
            iv: encryptedPayload.iv,
            encryptedAt: new Date().toISOString(),
          });
        } catch (error) {
          console.error("Failed to encrypt/store store credential:", error);
        }
      }
      setMultiStoreCredentials((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                status: "valid",
                statusMessage: "Credentials verified.",
              }
            : item,
        ),
      );
      return true;
    } catch (error) {
      console.error("Credential verification failed:", error);
      setMultiStoreCredentials((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                status: "invalid",
                statusMessage: "Could not verify right now. Please try again.",
              }
            : item,
        ),
      );
      return false;
    }
  };

  const handleMultiStoreSignin = async (e) => {
    e.preventDefault();
    console.log("[MultiStoreAuth] Sign in button clicked (form submit).", {
      isAnyCredentialChecking,
      stores: multiStoreCredentials.map((store) => ({
        id: store.id,
        email: store.email,
        status: store.status,
      })),
    });

    const storesToValidate = multiStoreCredentials.filter(
      (store) =>
        store.email.trim() && store.password.trim() && store.status !== "valid",
    );
    let validationResults = [];
    if (storesToValidate.length > 0) {
      validationResults = await Promise.all(
        storesToValidate.map((store) => validateMultiStoreCredential(store.id)),
      );
    }

    const hadInvalidFromThisRun = validationResults.some((result) => !result);
    const allPreviouslyValid = multiStoreCredentials.every(
      (store) =>
        store.status === "valid" ||
        storesToValidate.some((item) => item.id === store.id),
    );
    const allValid = allPreviouslyValid && !hadInvalidFromThisRun;
    if (!allValid) {
      setAuthError("Please verify all store credentials first.");
      return;
    }
    const firstStore = multiStoreCredentials[0];
    if (!firstStore?.email?.trim() || !firstStore?.password?.trim()) {
      setAuthError("First store credentials are missing.");
      return;
    }

    setIsSubmittingMultiStoreLogin(true);
    setAuthError("");
    setAuthEmail(firstStore.email.trim());
    setAuthPassword(firstStore.password);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: firstStore.email.trim(),
        password: firstStore.password,
      });
      if (error) throw error;
      await persistMultiStoreCredentialsForPin(
        multiStoreCredentials,
        multiStorePin,
      );
    } catch (error) {
      setAuthError(error.message || "Failed to sign in with first store.");
    } finally {
      setIsSubmittingMultiStoreLogin(false);
    }
  };

  if (authLoading) {
    return <MobileLoadingSplash logoSrc="/e.png" />;
  }

  if (session) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-blue-900 flex items-start justify-center p-4 sm:py-8">
      <div className="bg-white backdrop-blur-xl border border-slate-300 rounded-3xl p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl overflow-hidden mb-4 shadow-lg shadow-blue-500/20">
            <img
              src="/e.png"
              alt="Exitium"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Exitium Dashboard
          </h1>
          <p className="text-slate-500 text-center">
            {signInTab === "multi-store"
              ? "Sign in with multiple stores"
              : "Sign in to continue"}
          </p>
        </div>
        <div className="mb-6">
          <div className="grid grid-cols-2 rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setSignInTab("sign-in")}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                signInTab === "sign-in"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setSignInTab("multi-store")}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                signInTab === "multi-store"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Multiple stores
            </button>
          </div>
        </div>
        {signInTab === "sign-in" ? (
          <>
            <form
              onSubmit={handleLogin}
              className="space-y-4"
            >
              <div>
                <label className="block text-slate-500 text-sm mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block text-slate-500 text-sm mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  autoComplete={
                    "current-password"
                  }
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600"
                  required
                  minLength={6}
                />
              </div>
              {authError && (
                <div
                  className={`rounded-xl p-3 text-sm ${
                    authError.includes("Check your email")
                      ? "bg-emerald-100 border border-green-500/50 text-emerald-600"
                      : "bg-red-100 border border-red-500/50 text-red-300"
                  }`}
                >
                  {authError}
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-semibold py-3 rounded-xl hover:shadow-lg hover:scale-105 transition-all"
              >
                Sign In
              </button>
            </form>
            {/* No self sign-up. Accounts are created by Exitium for each
                store; the Supabase "Allow new users to sign up" setting is
                the real gate, this just stops advertising it. */}
          </>
        ) : (
          <form onSubmit={handleMultiStoreSignin} className="space-y-4">
            <div>
              <label className="block text-slate-500 text-sm mb-2">
                Enter a new pin code (6 digits)
              </label>
              <input
                type="password"
                value={multiStorePin}
                onChange={(e) =>
                  setMultiStorePin(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="Enter PIN"
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600"
                inputMode="numeric"
                pattern="\d{4,6}"
                minLength={6}
                maxLength={6}
                required
              />
              <p className="mt-2 text-xs text-slate-500">
                This PIN code is required when switching between stores.
              </p>
            </div>

            <div className="space-y-3">
              {multiStoreCredentials.map((store, index) => (
                <div
                  key={store.id}
                  className="border border-slate-300 rounded-xl p-4 space-y-3 bg-slate-50"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700">
                      Store {index + 1}
                    </p>
                    <div className="flex items-center gap-2">
                      {store.status === "checking" && (
                        <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                      )}
                      {store.status === "valid" && (
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      )}
                      {store.status === "invalid" && (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeMultiStoreCredential(store.id)}
                        className="text-xs text-red-500 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <input
                    type="email"
                    value={store.email}
                    onChange={(e) =>
                      updateMultiStoreCredential(
                        store.id,
                        "email",
                        e.target.value,
                      )
                    }
                    onBlur={() => validateMultiStoreCredential(store.id)}
                    placeholder="Store email"
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600"
                    required
                  />
                  <input
                    type="password"
                    value={store.password}
                    onChange={(e) =>
                      updateMultiStoreCredential(
                        store.id,
                        "password",
                        e.target.value,
                      )
                    }
                    onBlur={() => validateMultiStoreCredential(store.id)}
                    placeholder="Store password"
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-600"
                    required
                    minLength={6}
                  />
                  {store.statusMessage && (
                    <p
                      className={`text-xs ${
                        store.status === "valid"
                          ? "text-emerald-600"
                          : store.status === "invalid"
                            ? "text-red-500"
                            : "text-blue-600"
                      }`}
                    >
                      {store.statusMessage}
                    </p>
                  )}
                  {store.status === "checking" && (
                    <div className="flex items-center gap-2 text-xs text-blue-600">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Verifying credentials...</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addMultiStoreCredential}
              className="w-full border border-blue-600 text-blue-600 font-semibold py-2.5 rounded-xl hover:bg-blue-50 transition-all"
            >
              Add store
            </button>

            {authError && (
              <div className="rounded-xl p-3 text-sm bg-red-100 border border-red-500/50 text-red-300">
                {authError}
              </div>
            )}
            {isAnyCredentialChecking && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center gap-2 text-sm text-blue-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Verifying store credentials...</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isAnyCredentialChecking || isSubmittingMultiStoreLogin}
              onClick={() =>
                console.log("[MultiStoreAuth] Sign in button onClick fired.", {
                  isAnyCredentialChecking,
                  isSubmittingMultiStoreLogin,
                })
              }
              className="w-full bg-blue-600 text-white font-semibold py-3 rounded-xl hover:shadow-lg hover:scale-105 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
            >
              {isSubmittingMultiStoreLogin ? "Signing in..." : "Sign in"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReceiptDashboardAuthScreen;
